import {MenuItemRenderer} from '@rdcomponents/app/routing/helpers/menu-item-renderer';
import {ItemType} from 'antd/es/menu/interface';
import {ReactNode} from 'react';
import {Route} from 'react-router';
import {Redirect} from 'react-router-dom';

import {classNames} from '../../../../util/css';
import * as styles from '../../../navigation/navigation.scss';
import {nameForAnalytics} from '../../../navigation/navigation-type';
import {NavigationItemConfigType, NavigationItemRoutingType} from '../routing-type';

export function getAvailableItems(items: Array<NavigationItemConfigType>): Array<NavigationItemConfigType> {
    return items.flatMap((item) => {
        if (Reflect.has(item, 'isAvailable') && !item.isAvailable) {
            return [];
        }

        return [
            {
                ...item,
                ...(item.children
                    ? {
                          children: getAvailableItems(item.children),
                      }
                    : {}),
            },
        ];
    });
}

export function getRenderRoutes(routeItems: Array<NavigationItemConfigType>): Array<ReactNode> {
    return routeItems.map((route) => {
        const {exact, component, path, deniedPath, redirectPath, routes, search} = route;

        return [
            redirectPath &&
                deniedPath &&
                deniedPath.length > 0 &&
                deniedPath.map((navigateToPath) => {
                    return (
                        <Route exact key={navigateToPath} path={navigateToPath}>
                            <Redirect to={search ? {pathname: redirectPath, search} : redirectPath} />
                        </Route>
                    );
                }),
            path && component && <Route component={component} exact={exact} key={path} path={path} />,
            routes && routes.length > 0 && getRenderRoutes(routes),
        ];
    });
}

function buildMenuItemFromNavigationItem(navigationItem: NavigationItemConfigType): ItemType {
    const {isActivated, icon, children, path, className: itemClassName, key: itemKey, label} = navigationItem;
    const menuKey = itemKey ?? path;

    return {
        icon,
        className: classNames(itemClassName, {
            [styles.navigation__sub_menu]: Boolean(children),
            [styles.navigation__need_activation]: isActivated === false,
        }),
        label: <MenuItemRenderer navigationConfigItem={navigationItem} />,
        [nameForAnalytics]: navigationItem[nameForAnalytics],
        title: label,
        ...(menuKey
            ? {
                  key: menuKey,
              }
            : {
                  key: '',
              }),
        type: 'item',
    };
}

function buildCustomMenuItemFromNavigationItem(navigationItem: NavigationItemConfigType): ItemType {
    return {
        ...navigationItem,
        key:
            navigationItem.path ||
            navigationItem.key ||
            (navigationItem[nameForAnalytics] ? `custom-${navigationItem[nameForAnalytics]}` : ``),
        label: navigationItem.labelComponent ?? navigationItem.label,
        title: navigationItem.label,
        type: 'item',
    };
}

export function buildMenuItemsFromNavigationConfig(items: Array<NavigationItemConfigType>): Array<ItemType> {
    return items.flatMap((item, index) => {
        if (!item || item.isOnlyRoute) {
            return [];
        }

        const basicItem = item.isCustom
            ? buildCustomMenuItemFromNavigationItem(item)
            : buildMenuItemFromNavigationItem(item);

        if (item.children) {
            return {
                ...basicItem,
                key: item.key || `custom_${index}`,
                type: 'submenu',
                children: buildMenuItemsFromNavigationConfig(item.children),
            };
        }

        return [basicItem];
    });
}

export function navigationItemsToRouting(routes: Array<NavigationItemConfigType>): Array<NavigationItemRoutingType> {
    // eslint-disable-next-line complexity
    return routes.flatMap((route) => {
        const routeFromNavigationItem = {
            ...((route.path || route.key) && {path: route.path || route.key}),
            ...(route.component && {component: route.component}),
            ...('exact' in route && {exact: route.exact}),
            ...('search' in route && {search: route.search}),
            ...(route.deniedPath && {deniedPath: route.deniedPath}),
            ...(route.redirectPath && {redirectPath: route.redirectPath}),
            ...(route.children && {routes: navigationItemsToRouting(route.children)}),
        };

        if (Object.keys(routeFromNavigationItem).length > 0) {
            return [routeFromNavigationItem];
        }

        return [];
    });
}
